import { doc, updateDoc } from 'firebase/firestore'
import { db } from 'config/firebase'
import { Vehicle } from '@otw/models'
import { PrivateUserInfoType } from '@otw/models'
import { sendPhotoUploadLink } from '@/core/utils/sendPhotoUploadLink'
import { setAccessCode } from '@/core/photoUploader/setAccessCode'
import { createPhotoUpload } from '@/core/photoUploader/createPhotoUpload'
import { assignUserToVehicle } from '@/core/inventory/assignUserToVehicle'

export interface CreatePhotoUploadRequest {
  user: PrivateUserInfoType
  vehicles: Vehicle[]
  password: string
}

export interface CreatePhotoUploadResponse {
  photoUploadId: string
  accessCode: string
  link: string
  hasPhone: boolean
}

export const createPhotoUploadForUser = async ({
  user,
  vehicles,
  password
}: CreatePhotoUploadRequest): Promise<CreatePhotoUploadResponse> => {
  const vehicleIds = vehicles.map(v => v.id)
  
  // Assign user to all selected vehicles
  await Promise.all(
    vehicleIds.map(vehicleId => 
      assignUserToVehicle(vehicleId, user.id, 'master_inventory')
    )
  )

  // Create photo upload document
  const photoUploadId = await createPhotoUpload({
    userId: user.id,
    vehicleIds,
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
  })

  // Create access code with provided password
  const accessCode = password
  const uuid = await setAccessCode({
    accessCode,
    userId: user.id,
    vehicles: vehicles.map(v => ({
      id: v.id,
      vin: v.vin,
      make: v.make || '',
      model: v.model || '',
      year: v.year?.toString() || '',
    })),
    photoUploadId,
  })

  // Update photo upload document with access code
  const photoUploadRef = doc(db, 'photoUploads', photoUploadId)
  await updateDoc(photoUploadRef, {
    accessCode: uuid,
    updatedAt: new Date().toISOString()
  })

  // Generate access link
  const baseUrl = process.env.VITE_ENV === 'production' 
    ? 'https://uploader.optictruckworks.com/' 
    : 'https://stg.uploader.optictruckworks.com/'
  const link = `${baseUrl}/access/${uuid}`

  // Send link if phone number is available
  if (user.phone) {
    await sendPhotoUploadLink({
      link,
      phoneNumber: user.phone,
      email: user.email,
    })
  }

  return {
    photoUploadId,
    accessCode: uuid,
    link,
    hasPhone: !!user.phone
  }
} 